// src/pages/pengamatan/sensus-populasi-anakan-pisang/excelDownload.js
import * as XLSX from 'xlsx';

/**
 * Fungsi untuk mengkonversi tanggal DD-MM-YYYY ke Excel serial number
 */
const convertToExcelDate = (dateString) => {
  if (!dateString || dateString === '-') return null;
  
  try {
    const [day, month, year] = dateString.split('-');
    if (day && month && year) {
      // Buat objek Date dengan waktu 12:00:00 untuk menghindari masalah timezone
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      
      // Validasi apakah date valid
      if (isNaN(date.getTime())) return null;
      
      // Konversi ke Excel serial number YANG BENAR
      // Excel menggunakan 30 Des 1899 sebagai basis, bukan 1 Jan 1900
      const excelEpoch = new Date(1899, 11, 30); // 30 Desember 1899
      const diffTime = date - excelEpoch;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      
      return diffDays;
    }
  } catch (error) {
    console.error('Error converting date:', error);
  }
  return null;
};

/**
 * Fungsi untuk mengkonversi nilai ke number format
 */
const convertToNumber = (value) => {
  if (!value || value === '-' || value === '') return 0;
  
  // Jika sudah number, return langsung
  if (typeof value === 'number') return value;
  
  // Jika string, coba konversi ke number
  const numValue = parseFloat(value.toString().replace(',', '.'));
  return isNaN(numValue) ? 0 : numValue;
};

/**
 * Membuat headers untuk Excel
 */
const createExcelHeaders = () => {
  return [
    // Row 1
    [
      'Tanggal Pengamatan', // A1
      'Block',              // B1
      'Plot',               // C1
      'Luas',               // D1
      'Rumpun Tanpa Anakan', // E1
      'Rumpun Dengan Anakan', // F1 (merged F1-I1)
      '', '', '',           // G1, H1, I1 (bagian dari merge F1-I1)
      'Rumpun Dengan Anakan Tanpa Tagging', // J1 (merged J1-M1)
      '', '', '',           // K1, L1, M1 (bagian dari merge J1-M1)
      'Total',              // N1
      'Keterangan'          // O1
    ],
    // Row 2
    [
      '', // A2 (lanjutan dari A1)
      '', // B2 (lanjutan dari B1)
      '', // C2 (lanjutan dari C1)
      '', // D2 (lanjutan dari D1)
      '(0)', // E2
      '(1)', '(2)', '(3)', '(4)', // F2, G2, H2, I2
      '(1)', '(2)', '(3)', '(4)', // J2, K2, L2, M2
      '', // N2 (lanjutan dari N1)
      ''  // O2 (lanjutan dari O1)
    ]
  ];
};

/**
 * Membuat data rows untuk Excel
 */
const createDataRows = (selectedData) => {
  return selectedData.map(order => {
    // Untuk tanggal, gunakan string dengan format Excel-friendly
    let dateValue;
    if (order.tanggal_pengamatan && order.tanggal_pengamatan !== '-') {
      // Konversi dari DD-MM-YYYY ke format yang diinginkan Excel
      const [day, month, year] = order.tanggal_pengamatan.split('-');
      dateValue = `${day}/${month}/${year}`; // Format: 17/10/2025
    } else {
      dateValue = '-';
    }
    
    // Konversi nilai ke number format untuk kolom tertentu
    const luasValue = convertToNumber(order.luas);
    const rtaValue = convertToNumber(order.rta);
    const rda1Value = convertToNumber(order.rda_1);
    const rda2Value = convertToNumber(order.rda_2);
    const rda3Value = convertToNumber(order.rda_3);
    const rda4Value = convertToNumber(order.rda_4);
    const rdat1Value = convertToNumber(order.rdat_1);
    const rdat2Value = convertToNumber(order.rdat_2);
    const rdat3Value = convertToNumber(order.rdat_3);
    const rdat4Value = convertToNumber(order.rdat_4);
    const totalValue = convertToNumber(order.total);
    
    return [
      dateValue, // A: Tanggal sebagai string yang sudah diformat
      order.block || '-',                        // B
      order.plot || '-',                         // C
      luasValue,                                 // D: Luas (number)
      rtaValue,                                  // E: Rumpun Tanpa Anakan (number)
      rda1Value,                                 // F: Rumpun Dengan Anakan 1 (number)
      rda2Value,                                 // G: Rumpun Dengan Anakan 2 (number)
      rda3Value,                                 // H: Rumpun Dengan Anakan 3 (number)
      rda4Value,                                 // I: Rumpun Dengan Anakan 4 (number)
      rdat1Value,                                // J: Rumpun Dengan Anakan Tanpa Tagging 1 (number)
      rdat2Value,                                // K: Rumpun Dengan Anakan Tanpa Tagging 2 (number)
      rdat3Value,                                // L: Rumpun Dengan Anakan Tanpa Tagging 3 (number)
      rdat4Value,                                // M: Rumpun Dengan Anakan Tanpa Tagging 4 (number)
      totalValue,                                // N: Total (number)
      order.keterangan || '-'                    // O
    ];
  });
};

/**
 * Mengatur styling dan format untuk worksheet
 */
const setupWorksheetFormatting = (worksheet, excelData) => {
  // Set merge ranges untuk header
  if (!worksheet['!merges']) worksheet['!merges'] = [];
  
  // Merge ranges
  worksheet['!merges'].push({ s: { r: 0, c: 5 }, e: { r: 0, c: 8 } });
  worksheet['!merges'].push({ s: { r: 0, c: 9 }, e: { r: 0, c: 12 } });
  worksheet['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
  worksheet['!merges'].push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } });
  worksheet['!merges'].push({ s: { r: 0, c: 2 }, e: { r: 1, c: 2 } });
  worksheet['!merges'].push({ s: { r: 0, c: 3 }, e: { r: 1, c: 3 } });
  worksheet['!merges'].push({ s: { r: 0, c: 13 }, e: { r: 1, c: 13 } });
  worksheet['!merges'].push({ s: { r: 0, c: 14 }, e: { r: 1, c: 14 } });
  
  // Terapkan format tanggal secara manual ke seluruh kolom A
  for (let row = 2; row < excelData.length; row++) {
    const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
    if (worksheet[cellAddress]) {
      // Set format number untuk menghilangkan waktu
      worksheet[cellAddress].z = 'dd/mm/yyyy';
      
      // Jika value adalah string, konversi ke number (Excel serial)
      if (typeof worksheet[cellAddress].v === 'string' && worksheet[cellAddress].v !== '-') {
        const dateStr = worksheet[cellAddress].v;
        const [day, month, year] = dateStr.split('-');
        if (day && month && year) {
          // Konversi yang lebih sederhana dan akurat
          const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
          const excelEpoch = new Date(Date.UTC(1899, 11, 30));
          const diffDays = (date - excelEpoch) / (1000 * 60 * 60 * 24);
          
          worksheet[cellAddress].v = diffDays;
          worksheet[cellAddress].t = 'n';
        }
      }
    }
  }

  // Terapkan format number untuk kolom numeric dengan format yang berbeda
  for (let row = 2; row < excelData.length; row++) {
    // Kolom D (Luas) - dengan 2 digit desimal
    const luasCell = XLSX.utils.encode_cell({ r: row, c: 3 });
    if (worksheet[luasCell] && worksheet[luasCell].v !== null && worksheet[luasCell].v !== undefined) {
      worksheet[luasCell].t = 'n';
      worksheet[luasCell].z = '0.00'; // Format dengan 2 digit desimal
    }
    
    // Kolom E sampai N (Rumpun Tanpa Anakan, Rumpun Dengan Anakan, Rumpun Dengan Anakan Tanpa Tagging, Total) - tanpa desimal
    const integerColumns = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13]; // Kolom E sampai N
    
    integerColumns.forEach(col => {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      if (worksheet[cellAddress] && worksheet[cellAddress].v !== null && worksheet[cellAddress].v !== undefined) {
        worksheet[cellAddress].t = 'n';
        worksheet[cellAddress].z = '0'; // Format tanpa digit desimal
      }
    });
  }
  
  // Styling untuk header
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "4472C4" } },
    alignment: { horizontal: "center", vertical: "center" }
  };
  
  // Apply style ke header
  for (let col = 0; col < excelData[0].length; col++) {
    const cellAddr1 = XLSX.utils.encode_cell({ r: 0, c: col });
    const cellAddr2 = XLSX.utils.encode_cell({ r: 1, c: col });
    if (!worksheet[cellAddr1]) worksheet[cellAddr1] = {};
    if (!worksheet[cellAddr2]) worksheet[cellAddr2] = {};
    worksheet[cellAddr1].s = headerStyle;
    worksheet[cellAddr2].s = headerStyle;
  }
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 15 }, { wch: 20 }, { wch: 8 }, { wch: 8 }, { wch: 10 },
    { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
    { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
    { wch: 10 }, { wch: 15 }
  ];
};

/**
 * Fungsi utama untuk download Excel
 */
export const handleDownloadExcel = (selectedItems, orders, onStatusUpdate, onMenuClose) => {
  if (selectedItems.length === 0) return;
  
  try {
    // Filter data yang dipilih
    const selectedData = orders.filter(order => 
      selectedItems.includes(order.id)
    );

    // Buat headers dan data
    const headers = createExcelHeaders();
    const dataRows = createDataRows(selectedData);
    const excelData = [...headers, ...dataRows];
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Setup formatting
    setupWorksheetFormatting(worksheet, excelData);
    
    // Create workbook dan download
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sensus Populasi Anakan Pisang');
    const fileName = `SPA_Banana_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    onStatusUpdate({
      open: true,
      message: `Berhasil mendownload ${selectedItems.length} data ke Excel`,
      severity: 'success'
    });
    
    onMenuClose();
  } catch (error) {
    console.error('Error downloading Excel:', error);
    onStatusUpdate({
      open: true,
      message: 'Gagal mendownload data ke Excel',
      severity: 'error'
    });
  }
};

export default handleDownloadExcel;